import { isValidElement } from 'react';

import {
  QUESTIONNAIRE_OPTIONS,
  QUESTIONNAIRE_TYPES,
} from '../constants';

import type {
  IProviderAssignment,
  ISelectOption,
  SelectValueType,
} from '../../../../../../utils/types';
import type { QuestionnaireClientRef } from './types';

export function toClientOptions(assignments: IProviderAssignment[]): ISelectOption[] {
  const clientOptionsById = new Map<number, ISelectOption>();

  assignments.forEach(({ client }) => {
    if (!client?.id || clientOptionsById.has(client.id)) return;

    clientOptionsById.set(client.id, {
      label: `${client.first_name} ${client.last_name}`,
      value: client.id,
    });
  });

  return Array.from(clientOptionsById.values());
}

function withAlreadySentLabel(option: ISelectOption): ISelectOption {
  if (isValidElement(option.label)) {
    return { ...option, disabled: true };
  }

  return {
    ...option,
    disabled: true,
    label: `${option.label} (already sent)`,
  };
}

export function disableClientsWithBothQuestionnaires(
  clientOptions: ISelectOption[],
  onboardingQuestionnaires: readonly QuestionnaireClientRef[],
  intakeQuestionnaires: readonly QuestionnaireClientRef[],
): ISelectOption[] {
  const onboardingClientIds = new Set(
    onboardingQuestionnaires.map((questionnaire) => questionnaire.client_id),
  );
  const intakeClientIds = new Set(
    intakeQuestionnaires.map((questionnaire) => questionnaire.client_id),
  );

  return clientOptions.map((option) => {
    const clientId = Number(option.value);
    if (!onboardingClientIds.has(clientId) || !intakeClientIds.has(clientId)) {
      return option;
    }

    return withAlreadySentLabel(option);
  });
}

export function disableAlreadySentQuestionnaireTypes(
  selectedClientId: SelectValueType,
  onboardingQuestionnaires: readonly QuestionnaireClientRef[],
  intakeQuestionnaires: readonly QuestionnaireClientRef[],
): ISelectOption[] {
  if (selectedClientId === '') {
    return [...QUESTIONNAIRE_OPTIONS];
  }

  const clientId = Number(selectedClientId);
  const hasOnboarding = onboardingQuestionnaires.some(
    (questionnaire) => questionnaire.client_id === clientId,
  );
  const hasIntake = intakeQuestionnaires.some(
    (questionnaire) => questionnaire.client_id === clientId,
  );

  return QUESTIONNAIRE_OPTIONS.map((option) => {
    if (option.value === QUESTIONNAIRE_TYPES.ONBOARDING && hasOnboarding) {
      return withAlreadySentLabel(option);
    }
    if (option.value === QUESTIONNAIRE_TYPES.INTAKE && hasIntake) {
      return withAlreadySentLabel(option);
    }
    return option;
  });
}

export function isSelectOptionEnabled(
  options: readonly ISelectOption[],
  value: SelectValueType,
): boolean {
  return options.some((option) => option.value === value && !option.disabled);
}

export function hasEnabledSelectOption(options: readonly ISelectOption[]): boolean {
  return options.some((option) => !option.disabled);
}
